/**
 * Helpers partagés entre les sections documents.
 * Extrait de DocumentSections.js (refactor P2 — Feb 2026).
 */
import React from 'react';
import { Image, Video, FileText, FileArchive, File } from 'lucide-react';

export const getFileIcon = (mimeType) => {
  if (mimeType?.startsWith('image/')) return <Image className="h-4 w-4 text-purple-600" />;
  if (mimeType?.startsWith('video/')) return <Video className="h-4 w-4 text-red-600" />;
  if (mimeType?.includes('pdf')) return <FileText className="h-4 w-4 text-red-500" />;
  if (mimeType?.includes('zip') || mimeType?.includes('archive')) return <FileArchive className="h-4 w-4 text-yellow-600" />;
  return <File className="h-4 w-4 text-indigo-600" />;
};

export const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const getCategorieLabel = (cat) => {
  const labels = {
    'factures': 'Facture', 'dispositif_souscrit': 'Dispositif souscrit',
    'dommages': 'Dommages', 'proposition': 'Proposition',
    'conditions_generales': 'Conditions générales', 'devis': 'Devis',
    'photos': 'Photo', 'rapport': 'Rapport', 'courrier': 'Courrier',
    'piece_identite': "Pièce d'identité", 'contrat': 'Contrat',
    'attestation': 'Attestation', 'autre': 'Autre',
  };
  return labels[cat] || cat || 'Document';
};

// Types considérés comme "Propositions ESS" (devis générés par BATICO/ESS).
export const PROPOSITION_TYPES = new Set([
  'devis_ess', 'devis_ess_genere', 'proposition', 'devis',
]);

export function formatEUR(amount) {
  if (amount === null || amount === undefined || amount === '') return null;
  const num = typeof amount === 'number' ? amount : parseFloat(amount);
  if (Number.isNaN(num)) return null;
  return num.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function extractMontantTTC(doc) {
  // V35.65 — Chemins étendus pour gérer les propositions BATICO issues du
  // simulateur (snapshot_payload.tarification.montant_ttc) et les anciens
  // formats. Ordre : direct → metadata → snapshot → description regex.
  if (!doc) return null;
  const candidates = [
    doc.montant_global,
    doc.montant_ttc,
    doc.montant_total,
    doc.montant_total_ttc,
    doc.amount_ttc,
    doc.metadata && doc.metadata.montant_global,
    doc.metadata && doc.metadata.montant_ttc,
    doc.metadata && doc.metadata.montant_total,
    doc.tarification && doc.tarification.montant_ttc,
    doc.tarification && doc.tarification.montant_total_ttc,
    doc.snapshot && doc.snapshot.tarification && doc.snapshot.tarification.montant_ttc,
    doc.snapshot_payload && doc.snapshot_payload.tarification && doc.snapshot_payload.tarification.montant_ttc,
    doc.metadata && doc.metadata.snapshot && doc.metadata.snapshot.tarification && doc.metadata.snapshot.tarification.montant_ttc,
    doc.metadata && doc.metadata.snapshot_payload && doc.metadata.snapshot_payload.tarification && doc.metadata.snapshot_payload.tarification.montant_ttc,
  ];
  for (const c of candidates) {
    if (c === null || c === undefined || c === '') continue;
    const num = typeof c === 'number' ? c : parseFloat(String(c).replace(/[\s ]/g, '').replace(',', '.'));
    if (!Number.isNaN(num) && num > 0) return num;
  }
  const desc = doc.description || doc.message || '';
  const match = desc.match(/Montant\s*(?:TTC|HT|Global)?\s*[:=]\s*([\d\s.,]+)\s*€?/i);
  if (match && match[1]) {
    const cleaned = match[1].replace(/\s+/g, '').replace(',', '.');
    const num = parseFloat(cleaned);
    if (!Number.isNaN(num) && num > 0) return num;
  }
  return null;
}

export function extractValidite(doc) {
  const raw = doc.date_expiration
    || doc.date_fin
    || (doc.metadata && doc.metadata.date_validite)
    || null;
  if (!raw) {
    const dateCreation = doc.date_creation || doc.created_at;
    const valideJours = (doc.metadata && doc.metadata.validite_jours) || null;
    if (dateCreation && valideJours) {
      const d = new Date(dateCreation);
      d.setDate(d.getDate() + parseInt(valideJours, 10));
      const diffDays = Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
      return { date: d, joursRestants: diffDays, expired: diffDays < 0 };
    }
    return { date: null, joursRestants: null, expired: false };
  }
  let d;
  try {
    if (typeof raw === 'string' && raw.includes('/')) {
      const [dd, mm, yy] = raw.split('/');
      d = new Date(yy, mm - 1, dd);
    } else {
      d = new Date(raw);
    }
  } catch { return { date: null, joursRestants: null, expired: false }; }
  const diffDays = Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
  return { date: d, joursRestants: diffDays, expired: diffDays < 0 };
}
